// 200 ms route transitions for the admin (owner spec 2026-10-01).
//
// • Taps on the shell's own navigation (bottom tabs, « More », desktop menu)
//   go through navigateWithTransition: document.startViewTransition when the
//   browser has it — a cross-fade with the header and the tab bar standing
//   still. BrowserRouter commits a navigation inside React.startTransition, so
//   the update callback cannot flushSync it; it navigates and resolves as soon
//   as the shell sees the new route committed (routeCommitted), and in any
//   case after 300 ms — the screen is never held longer than that.
// • Every other navigation (Back, swipe-back, links inside pages, a
//   notification tap) gets a light entrance on the page content instead:
//   slide from the right going forward, from the left going back.
// • prefers-reduced-motion: no animation at all. Navigation is never delayed
//   beyond the one frame the View Transition needs to take its snapshot.

export const ROUTE_MS = 200;
const HOLD_MAX_MS = 300;

export type NavType = 'PUSH' | 'POP' | 'REPLACE';

export function entranceKeyframes(type: NavType): Keyframe[] {
  if (type === 'REPLACE') return [{ opacity: 0 }, { opacity: 1 }];
  const from = type === 'PUSH' ? 'translateX(16px)' : 'translateX(-16px)';
  return [{ opacity: 0, transform: from }, { opacity: 1, transform: 'none' }];
}

export function prefersReducedMotion(win: Pick<Window, 'matchMedia'> | undefined = typeof window !== 'undefined' ? window : undefined): boolean {
  try { return !!win?.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

interface DocLike {
  startViewTransition?: ((update: () => Promise<void> | void) => unknown) | undefined;
}

let pending: (() => void) | null = null;
let viewTransitionRan = false;

export function navigateWithTransition(go: () => void, doc: DocLike = document as unknown as DocLike, reduced: boolean = prefersReducedMotion()): void {
  if (reduced || typeof doc.startViewTransition !== 'function') { go(); return; }
  try {
    doc.startViewTransition(() => new Promise<void>((resolve) => {
      let settled = false;
      const done = () => { if (!settled) { settled = true; pending = null; resolve(); } };
      pending = done;
      viewTransitionRan = true;
      setTimeout(done, HOLD_MAX_MS);
      go();
    }));
  } catch {
    go();
  }
}

/** The shell calls this from a layout effect once the new route is on screen. */
export function routeCommitted(): void {
  pending?.();
}

/** True once after a View Transition navigation: the page entrance skips it. */
export function takeViewTransitionFlag(): boolean {
  const v = viewTransitionRan;
  viewTransitionRan = false;
  return v;
}
